// Shared "errors for strategy X" source (plan 2026-06-14-002 — /simplify consolidation). The Errors tab
// (full table), the Overview banner (newest error), and the workspace tab badge (count) all need the SAME
// deduped, severity-gated, newest-first list: the durable REST history (GET /{id}/errors) merged with the
// live autotradeAlerts SSE feed, deduped by `seq`. This module owns that rule ONCE so the three consumers
// can't drift (the badge count silently disagreeing with the tab contents was the risk of three copies).
//
// One REST fetch per (strategyId, orgId) is shared across all consumers via a small cache, instead of the
// badge + banner + tab each issuing their own identical request.

import { derived, writable } from 'svelte/store';
import api from './api.js';
import { autotradeAlerts } from './sseStore.js';

/** The severities that count as a loud "error" — the banner + Errors view gate on these (>= Error). */
export function isErrorSeverity(sev) {
  return sev === 'Error' || sev === 'Critical';
}

/** The severities that count as an "alert" — the Alerts tab gates on these (< Error: Info/Warning). #3 */
export function isAlertSeverity(sev) {
  return sev === 'Info' || sev === 'Warning';
}

/** Render a timestamp, tolerating an invalid/missing value (the shared AutoTrading time format). */
export function fmtErrorTime(t) {
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Per-(strategyId, orgId) REST-history cache: { store, promise }. Keeps the three consumers (badge, banner,
// tab) on one fetch. Bounded so a long session visiting many strategies/orgs can't grow it without limit —
// evict the oldest entry (insertion order) once over the cap. The live SSE merge keeps active views fresh
// regardless; an evicted entry just re-fetches its REST history on next access.
const historyCache = new Map();
const MAX_CACHE_ENTRIES = 64;

// The two durable feeds — Errors (severity >= Error) and Alerts (Info/Warning), each its own REST endpoint.
const FEED_FETCHERS = {
  errors: (id, orgId) => api.getStrategyErrors(id, orgId),
  alerts: (id, orgId) => api.getStrategyAlerts(id, orgId),
};

function keyFor(feed, strategyId, orgId) {
  return `${feed}|${strategyId}|${orgId ?? ''}`;
}

// Shared per-(feed, strategy, org) REST-history loader: one fetch shared by every consumer (badge, banner, tab),
// LRU-bounded. `feed` is 'errors' or 'alerts'.
function ensureHistory(feed, strategyId, orgId) {
  const key = keyFor(feed, strategyId, orgId);
  let entry = historyCache.get(key);
  if (entry) return entry;

  if (historyCache.size >= MAX_CACHE_ENTRIES) {
    historyCache.delete(historyCache.keys().next().value); // evict oldest (Map preserves insertion order)
  }

  const store = writable([]);
  // A single fetch shared by every consumer of this (feed, strategy, org). A 404/error → empty (the banner
  // shows nothing; the tab surfaces its own inline error via the reload helper if the user retries).
  const promise = FEED_FETCHERS[feed](strategyId, orgId)
    .then((rows) => {
      store.set(Array.isArray(rows) ? rows : []);
    })
    .catch(() => {
      store.set([]);
    });

  entry = { store, promise };
  historyCache.set(key, entry);
  return entry;
}

// Normalize a REST row (carries `createdAt`) and an SSE alert (carries `at`) to one render shape + sort key.
function fromHistory(r) {
  return { seq: r.seq, code: r.code, message: r.message, severity: r.severity, time: r.createdAt };
}
function fromAlert(a) {
  return { seq: a.seq, code: a.code, message: a.message, severity: a.severity, time: a.at };
}

// Build a derived store for one feed: REST history (authoritative) merged with the live SSE autotradeAlerts
// (this strategy, severities matching the feed's `severityMatch`), deduped by `seq`, newest-first.
function feedStore(feed, severityMatch, strategyId, orgId) {
  if (!strategyId) return writable([]);

  const { store: history } = ensureHistory(feed, strategyId, orgId);

  return derived([history, autotradeAlerts], ([$history, $alerts]) => {
    const byKey = new Map();
    // Key by seq when present (the server's insertion-monotonic id), else a per-row synthetic key so two
    // seq-less rows don't collapse into one (a missing seq must never silently drop a real row).
    let synthetic = 0;
    const keyOf = (row) => (row.seq ? `s${row.seq}` : `x${synthetic++}`);
    for (const r of $history) byKey.set(keyOf(r), fromHistory(r));
    const seenSeqs = new Set($history.map((r) => r.seq).filter(Boolean));
    for (const a of $alerts) {
      // REST is authoritative — only append a live row the history hasn't already delivered (dedup by seq).
      if (a.strategyId === strategyId && severityMatch(a.severity) && !(a.seq && seenSeqs.has(a.seq))) {
        byKey.set(keyOf(a), fromAlert(a));
      }
    }
    return [...byKey.values()].sort((x, y) => y.seq - x.seq);
  });
}

/**
 * A derived store of the strategy's ERRORS (severity >= Error): REST history merged with the live SSE feed,
 * deduped by `seq`, newest-first. Fetched once per (strategyId, orgId) and shared. Empty store when no id.
 */
export function strategyErrors(strategyId, orgId) {
  return feedStore('errors', isErrorSeverity, strategyId, orgId);
}

/**
 * A derived store of the strategy's ALERTS (Info/Warning — the Errors complement): REST history merged with the
 * live SSE feed, deduped, newest-first. Powers the Alerts tab (#3). Empty store when no id.
 */
export function strategyAlerts(strategyId, orgId) {
  return feedStore('alerts', isAlertSeverity, strategyId, orgId);
}

/** Force-refresh the ERRORS REST history (Errors tab Refresh/Retry). Returns the fetch promise. */
export function reloadStrategyErrors(strategyId, orgId) {
  return reloadFeed('errors', strategyId, orgId);
}

/** Force-refresh the ALERTS REST history (Alerts tab Refresh/Retry). Returns the fetch promise. */
export function reloadStrategyAlerts(strategyId, orgId) {
  return reloadFeed('alerts', strategyId, orgId);
}

function reloadFeed(feed, strategyId, orgId) {
  if (!strategyId) return Promise.resolve();
  const { store } = ensureHistory(feed, strategyId, orgId);
  return FEED_FETCHERS[feed](strategyId, orgId).then((rows) => {
    store.set(Array.isArray(rows) ? rows : []);
  });
}

/**
 * The most-recent-bar badge counts ({ errorCount, alertCount, barTime }) for (strategyId, orgId), refreshed when
 * `refreshKey` changes (the host bumps it on each refetch / SSE tick) so the badges track the latest bar window
 * and reset as a newer bar advances. Returns a store of { errorCount, alertCount, barTime }.
 */
export function recentBarCounts(strategyId, orgId, refreshKey) {
  const store = writable({ errorCount: 0, alertCount: 0, barTime: null });
  if (!strategyId) return store;
  api
    .getAlertCounts(strategyId, orgId)
    .then((c) => store.set(c && typeof c === 'object'
      ? { errorCount: c.errorCount ?? 0, alertCount: c.alertCount ?? 0, barTime: c.barTime ?? null }
      : { errorCount: 0, alertCount: 0, barTime: null }))
    .catch(() => store.set({ errorCount: 0, alertCount: 0, barTime: null }));
  return store;
}
